import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface ArvoreNode {
  id: string;
  geracao: string;
  sexo: string;
  nome: string;
  localNascimento: string;
  dataNascimento: string;
  localCasamento: string;
  dataCasamento: string;
  localObito: string;
  dataObito: string;
  NomePai: string;
  PaiNascimento: string;
  NomeMae: string;
  MaeNascimento: string;
  observacao: string;
  marcarNo: boolean;
  filhos: ArvoreNode[];
}
type CampoDataArvore = 'dataNascimento' | 'dataCasamento' | 'dataObito';

@Component({
  selector: 'app-arvore-genealogica',
  templateUrl: './arvore-genealogica.component.html',
  styleUrls: ['./arvore-genealogica.component.scss']
})
export class ArvoreGenealogicaComponent implements OnInit {
  @ViewChild('fullscreenContainer', { static: false }) fullscreenContainerRef: ElementRef<HTMLDivElement>;
  @ViewChild('treeViewport', { static: false }) treeViewportRef: ElementRef<HTMLDivElement>;
  @ViewChild('treeContent', { static: false }) treeContentRef: ElementRef<HTMLDivElement>;

  treeData: ArvoreNode = this.criarNoPadrao('G1');
  fitScale = 1;
  userZoom = 1;
  treeScale = 1;
  treeOffsetX = 0;
  treeOffsetY = 0;
  loading = false;
  idConsulta: string = '';
  idPaciente: string = '';

  modalVisible = false;
  noSelecionado: ArvoreNode | null = null;
  isFullscreen = false;
  modoLegivel = false;
  isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panStartOffsetX = 0;
  private panStartOffsetY = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.idConsulta = params.get('idConsulta') || '';
      this.idPaciente = params.get('idPaciente') || '';
      this.carregarArvore();
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.ajustarEscalaArvore(true);
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = !!document.fullscreenElement;
    this.ajustarEscalaArvore(true);
  }

  @HostListener('document:mouseup')
  onGlobalMouseUp(): void {
    this.finalizarPan();
  }

  abrirEdicaoNo(node: ArvoreNode): void {
    this.noSelecionado = node;
    this.modalVisible = true;
  }

  fecharModal(): void {
    this.modalVisible = false;
    this.noSelecionado = null;
  }

  adicionarFilho(node: ArvoreNode): void {
    if (!node.filhos) node.filhos = [];
    if (node.filhos.length >= 2) {
      this.util.toasterShowError('Atenção', 'Cada nó permite até 2 filhos na visualização.');
      return;
    }

    const novaGeracao = this.proximaGeracao(node.geracao);
    node.filhos.push(this.criarNoPadrao(novaGeracao));
    this.ajustarEscalaArvore(false);
  }

  removerFilhos(node: ArvoreNode): void {
    if (!node.filhos?.length) return;
    if (!confirm('Tem certeza que deseja remover os descendentes deste nó?')) return;
    node.filhos = [];
    this.ajustarEscalaArvore(false);
  }

  salvarArvore(): void {
    const payload = {
      idConsulta: this.idConsulta || null,
      idPaciente: this.idPaciente || null,
      arvore: JSON.stringify([this.treeData])
    };

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.post(API + 'empresa/Consulta/SalvarArvoreEstudo', payload).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível salvar a árvore.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Árvore genealógica salva com sucesso!');
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao salvar a árvore genealógica.');
      }
    });
  }

  voltar(): void {
    this.router.navigate(['/consultar-atendimento']);
  }

  alternarTelaCheia(): void {
    const container = this.fullscreenContainerRef?.nativeElement;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen?.()
        .then(() => {
          this.isFullscreen = true;
          this.ajustarEscalaArvore(true);
        })
        .catch(() => {
          this.util.toasterShowError('Erro', 'Não foi possível entrar em tela cheia.');
        });
      return;
    }

    document.exitFullscreen?.()
      .then(() => {
        this.isFullscreen = false;
        this.ajustarEscalaArvore(true);
      })
      .catch(() => {
        this.util.toasterShowError('Erro', 'Não foi possível sair da tela cheia.');
      });
  }

  private carregarArvore(): void {
    const id = this.idConsulta || this.idPaciente;
    if (!id) {
      this.treeData = this.criarNoPadrao('G1');
      this.ajustarEscalaArvore(false);
      return;
    }

    const endpoint = this.idConsulta
      ? API + `empresa/Consulta/VerificarSeExisteArvore?id=${encodeURIComponent(this.idConsulta)}`
      : API + `empresa/Consulta/VerificarSeExisteArvorePaciente?id=${encodeURIComponent(this.idPaciente)}`;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(endpoint).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();

        const arvoreRaw = response?.data?.arvore || response?.data?.ARVORE || '';
        if (arvoreRaw && typeof arvoreRaw === 'string' && arvoreRaw.trim()) {
          this.treeData = this.parseArvore(arvoreRaw);
          this.ajustarEscalaArvore(false);
          return;
        }

        this.treeData = this.criarNoPadrao('G1');
        this.ajustarEscalaArvore(false);
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.treeData = this.criarNoPadrao('G1');
        this.ajustarEscalaArvore(false);
      }
    });
  }

  private parseArvore(arvoreRaw: string): ArvoreNode {
    try {
      const parsed = JSON.parse(arvoreRaw);
      const base = Array.isArray(parsed) ? parsed[0] : parsed;
      const no = this.normalizarNo(base || this.criarNoPadrao('G1'));
      this.ajustarEscalaArvore(false);
      return no;
    } catch {
      return this.criarNoPadrao('G1');
    }
  }

  private normalizarNo(input: any): ArvoreNode {
    const filhos = Array.isArray(input?.filhos) ? input.filhos.map((f: any) => this.normalizarNo(f)) : [];
    return {
      id: String(input?.id ?? ''),
      geracao: String(input?.geracao ?? ''),
      sexo: String(input?.sexo ?? ''),
      nome: String(input?.nome ?? ''),
      localNascimento: String(input?.localNascimento ?? ''),
      dataNascimento: String(input?.dataNascimento ?? ''),
      localCasamento: String(input?.localCasamento ?? ''),
      dataCasamento: String(input?.dataCasamento ?? ''),
      localObito: String(input?.localObito ?? ''),
      dataObito: String(input?.dataObito ?? ''),
      NomePai: String(input?.NomePai ?? ''),
      PaiNascimento: String(input?.PaiNascimento ?? ''),
      NomeMae: String(input?.NomeMae ?? ''),
      MaeNascimento: String(input?.MaeNascimento ?? ''),
      observacao: String(input?.observacao ?? ''),
      marcarNo: !!input?.marcarNo,
      filhos
    };
  }

  private criarNoPadrao(geracao: string): ArvoreNode {
    return {
      id: '',
      geracao,
      sexo: '',
      nome: 'Novo membro',
      localNascimento: '',
      dataNascimento: '',
      localCasamento: '',
      dataCasamento: '',
      localObito: '',
      dataObito: '',
      NomePai: '',
      PaiNascimento: '',
      NomeMae: '',
      MaeNascimento: '',
      observacao: '',
      marcarNo: false,
      filhos: []
    };
  }

  private proximaGeracao(geracaoAtual: string): string {
    const numero = Number((geracaoAtual || '').replace(/\D/g, '')) || 1;
    return `G${numero + 1}`;
  }

  iniciarPan(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.node-card, .node-actions, button, input, textarea, select, label')) return;

    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.panStartOffsetX = this.treeOffsetX;
    this.panStartOffsetY = this.treeOffsetY;
  }

  moverPan(event: MouseEvent): void {
    if (!this.isPanning) return;
    this.treeOffsetX = this.panStartOffsetX + (event.clientX - this.panStartX);
    this.treeOffsetY = this.panStartOffsetY + (event.clientY - this.panStartY);
  }

  finalizarPan(): void {
    this.isPanning = false;
  }

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    const fator = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    this.aplicarZoom(fator, event.clientX, event.clientY);
  }

  zoomIn(): void {
    this.aplicarZoom(1.15);
  }

  zoomOut(): void {
    this.aplicarZoom(1 / 1.15);
  }

  resetarZoom(): void {
    this.userZoom = 1;
    this.ajustarEscalaArvore(false);
  }

  alternarModoLegivel(): void {
    this.modoLegivel = !this.modoLegivel;
    this.ajustarEscalaArvore(true);
  }

  getDescricaoGeracao(geracao: string): string {
    const numero = Number((geracao || '').replace(/\D/g, ''));
    if (!numero) {
      return geracao || 'Geração';
    }

    const mapa: { [key: number]: string } = {
      1: 'Você',
      2: 'Pai / Mãe',
      3: 'Avô / Avó',
      4: 'Bisavô / Bisavó',
      5: 'Trisavô / Trisavó'
    };

    return mapa[numero] || `Geração ${numero}`;
  }

  isNoCompacto(node: ArvoreNode): boolean {
    const nome = this.textoLimpo(node?.nome);
    const hasNomePreenchido = !!nome && nome.toLowerCase() !== 'novo membro';
    const hasFiliacao = !!this.textoLimpo(node?.NomePai) || !!this.textoLimpo(node?.NomeMae);
    const hasNascimento = !!this.textoLimpo(node?.dataNascimento) || !!this.textoLimpo(node?.localNascimento);
    const hasCasamento = !!this.textoLimpo(node?.dataCasamento) || !!this.textoLimpo(node?.localCasamento);
    const hasObito = !!this.textoLimpo(node?.dataObito) || !!this.textoLimpo(node?.localObito);
    const hasObservacao = !!this.textoLimpo(node?.observacao);
    return !(hasNomePreenchido || hasFiliacao || hasNascimento || hasCasamento || hasObito || hasObservacao);
  }

  temFiliacao(node: ArvoreNode): boolean {
    return !!this.textoLimpo(node?.NomePai) || !!this.textoLimpo(node?.NomeMae);
  }

  temNascimento(node: ArvoreNode): boolean {
    return !!this.textoLimpo(node?.dataNascimento) || !!this.textoLimpo(node?.localNascimento);
  }

  temCasamento(node: ArvoreNode): boolean {
    return !!this.textoLimpo(node?.dataCasamento) || !!this.textoLimpo(node?.localCasamento);
  }

  temObito(node: ArvoreNode): boolean {
    return !!this.textoLimpo(node?.dataObito) || !!this.textoLimpo(node?.localObito);
  }

  onDataChange(campo: CampoDataArvore, valor: string): void {
    if (!this.noSelecionado) return;
    this.noSelecionado[campo] = this.formatarData(valor);
  }

  private textoLimpo(value: string | null | undefined): string {
    return String(value || '').trim();
  }

  private formatarData(valor: string): string {
    const numeros = String(valor || '').replace(/\D/g, '').slice(0, 8);
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 4) return `${numeros.slice(0, 2)}/${numeros.slice(2)}`;
    return `${numeros.slice(0, 2)}/${numeros.slice(2, 4)}/${numeros.slice(4)}`;
  }

  private aplicarZoom(fator: number, clientX?: number, clientY?: number): void {
    const viewport = this.treeViewportRef?.nativeElement;
    if (!viewport || !this.fitScale) return;

    const scaleAntiga = this.treeScale || 1;
    const zoomMin = Math.max(0.35 / this.fitScale, 0.5);
    const zoomMax = Math.max(2.5 / this.fitScale, 2);
    this.userZoom = Math.max(zoomMin, Math.min(zoomMax, this.userZoom * fator));
    this.treeScale = Math.max(0.35, Math.min(3, this.fitScale * this.userZoom));

    const rect = viewport.getBoundingClientRect();
    const pontoX = clientX !== undefined ? (clientX - rect.left) : viewport.clientWidth / 2;
    const pontoY = clientY !== undefined ? (clientY - rect.top) : viewport.clientHeight / 2;
    const contentX = (pontoX - this.treeOffsetX) / scaleAntiga;
    const contentY = (pontoY - this.treeOffsetY) / scaleAntiga;

    this.treeOffsetX = pontoX - (contentX * this.treeScale);
    this.treeOffsetY = pontoY - (contentY * this.treeScale);
  }

  private ajustarEscalaArvore(preservarVisualizacao: boolean): void {
    setTimeout(() => {
      const viewport = this.treeViewportRef?.nativeElement;
      const content = this.treeContentRef?.nativeElement;
      if (!viewport || !content) return;

      const larguraViewport = Math.max(0, viewport.clientWidth - 8);
      const larguraConteudo = content.scrollWidth;
      if (!larguraViewport || !larguraConteudo) {
        this.fitScale = 1;
        this.userZoom = 1;
        this.treeScale = 1;
        this.treeOffsetX = 0;
        this.treeOffsetY = 0;
        return;
      }

      const escalaAntiga = this.treeScale || 1;
      this.fitScale = Math.min(1, larguraViewport / larguraConteudo);
      if (!preservarVisualizacao) {
        this.userZoom = 1;
      } else {
        const zoomAtual = escalaAntiga / Math.max(this.fitScale, 0.0001);
        const zoomMin = Math.max(0.35 / this.fitScale, 0.5);
        const zoomMax = Math.max(2.5 / this.fitScale, 2);
        this.userZoom = Math.max(zoomMin, Math.min(zoomMax, zoomAtual));
      }

      this.treeScale = Math.max(0.35, Math.min(3, this.fitScale * this.userZoom));

      // Centraliza pelo nó raiz (comportamento visual da tela antiga),
      // em vez de centralizar pela largura total da árvore.
      const rootNode = content.querySelector('.tree-root > li > .node-row') as HTMLElement | null;
      if (rootNode) {
        const centroRaiz = rootNode.offsetLeft + (rootNode.offsetWidth / 2);
        this.treeOffsetX = Math.floor((larguraViewport / 2) - (centroRaiz * this.treeScale));
      } else {
        this.treeOffsetX = Math.floor((larguraViewport - (larguraConteudo * this.treeScale)) / 2);
      }

      if (!preservarVisualizacao) {
        this.treeOffsetY = 0;
      }
    });
  }
}
